import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { RiEyeLine, RiEyeOffLine } from "react-icons/ri";
import FieldToast from "../components/FieldToast";
import "./CreateUser.css";
import { createUser } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_VALUES, formatRoleLabel, hasRole } from "../utils/roles";

const mapRoleToApi = (role) => {
  switch (String(role).toLowerCase().trim()) {
    case "founder":      return "Founder";
    case "manager":      return "Manager";
    case "team leader":
    case "team_leader":  return "Team Leader";
    default:             return "Employee";
  }
};

export default function CreateUser() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isPublicSignup = !currentUser;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phoneNumber: "",
    role: ROLE_VALUES.EMPLOYEE,
    department: "",
    position: "",
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Field refs for validation toasts
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const phoneRef = useRef(null);
  const deptRef = useRef(null);
  const positionRef = useRef(null);

  // Field toast state
  const [fieldToast, setFieldToast] = useState({
    show: false,
    message: "",
    targetRef: null,
  });

  const showFieldToast = (message, targetRef) => {
    setFieldToast({
      show: true,
      message,
      targetRef,
    });
    setTimeout(() => {
      setFieldToast((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "department") {
      setFormData({ ...formData, department: value, position: "" });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Name validation
    if (!formData.name || formData.name.trim() === "") {
      showFieldToast("Name is required", nameRef);
      return;
    }

    if (formData.name.trim().length < 3) {
      showFieldToast("Name must be at least 3 characters", nameRef);
      return;
    }

    if (formData.name.trim().length > 50) {
      showFieldToast("Name must be less than 50 characters", nameRef);
      return;
    }

    if (!/^[a-zA-Z\u0600-\u06FF\s]+$/.test(formData.name.trim())) {
      showFieldToast("Name must contain only letters (English or Arabic)", nameRef);
      return;
    }

    // Email validation
    if (!formData.email || formData.email.trim() === "") {
      showFieldToast("Email is required", emailRef);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      showFieldToast("Please enter a valid email address", emailRef);
      return;
    }

    // Password validation
    if (!formData.password || formData.password.trim() === "") {
      showFieldToast("Password is required", passwordRef);
      return;
    }

    if (formData.password.length < 6) {
      showFieldToast("Password must be at least 6 characters", passwordRef);
      return;
    }

    if (formData.password.length > 30) {
      showFieldToast("Password must be less than 30 characters", passwordRef);
      return;
    }

    // Phone number validation
    if (!formData.phoneNumber || formData.phoneNumber.trim() === "") {
      showFieldToast("Phone number is required", phoneRef);
      return;
    }

    const phoneRegex = /^(\+20|0020|0)?1[0125][0-9]{8}$/;
    if (!phoneRegex.test(formData.phoneNumber.trim())) {
      showFieldToast("Please enter a valid Egyptian phone number\nExample: 01012345678", phoneRef);
      return;
    }

    // Department validation
    if (!formData.department || formData.department.trim() === "") {
      showFieldToast("Department is required", deptRef);
      return;
    }

    // Position validation (if not Founder)
    if (!hasRole(formData.role, ROLE_VALUES.FOUNDER) && (!formData.position || formData.position.trim() === "")) {
      showFieldToast("Position is required", positionRef);
      return;
    }

    setLoading(true);

    try {
      const apiData = {
        userName:    formData.name.trim(),
        email:       formData.email.trim(),
        password:    formData.password,
        phoneNumber: formData.phoneNumber.trim(),
        department:  formData.department,
        role:        mapRoleToApi(formData.role),
      };

      if (hasRole(formData.role, ROLE_VALUES.FOUNDER)) {
        apiData.department = "CEO";
        apiData.position   = "Other";
      } else if (formData.position) {
        apiData.position = formData.position;
      } else {
        apiData.position = "Other";
      }

      await createUser(apiData);
      toast.success(
        isPublicSignup
          ? "Account created successfully. Please log in."
          : "User created successfully"
      );

      setFormData({
        name: "", email: "", password: "", phoneNumber: "",
        role: ROLE_VALUES.EMPLOYEE, department: "", position: "",
      });

      navigate(isPublicSignup ? "/login" : "/users", { replace: true });

    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.response?.data || "Something went wrong";
      toast.error(Array.isArray(errorMsg) ? errorMsg.join("\n") : errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-user-page">
      <div className="create-user-card">
        <h1 className="create-user-title">Create New User</h1>

        <form onSubmit={handleSubmit} className="create-user-form">
          <input
            ref={nameRef}
            name="name"
            type="text"
            placeholder="Full Name *"
            value={formData.name}
            onChange={handleChange}
          />

          <input
            ref={emailRef}
            name="email"
            type="email"
            placeholder="Email *"
            value={formData.email}
            onChange={handleChange}
          />

          <div className="password-input-wrapper">
            <input
              ref={passwordRef}
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Temporary Password *"
              value={formData.password}
              onChange={handleChange}
            />
            <button
              type="button"
              className="toggle-password-btn"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
            </button>
          </div>

          <input
            ref={phoneRef}
            name="phoneNumber"
            type="tel"
            placeholder="Phone Number * (e.g. 01012345678)"
            value={formData.phoneNumber}
            onChange={handleChange}
          />

          <select ref={deptRef} name="department" value={formData.department} onChange={handleChange}>
            <option value="">Select Department *</option>
            <option value="Software">Software</option>
            <option value="Marketing">Marketing</option>
            <option value="CEO">CEO</option>
            <option value="COO">COO</option>
            <option value="HR">HR</option>
            <option value="Other">Other</option>
          </select>

          {!hasRole(formData.role, ROLE_VALUES.FOUNDER) && (
            <select ref={positionRef} name="position" value={formData.position} onChange={handleChange}>
              <option value="">Select Position</option>

              {formData.department === "Software" && (<>
                <option value="Frontend Developer">Frontend Developer</option>
                <option value="Backend Developer">Backend Developer</option>
                <option value="UI/UX Designer">UI/UX Designer</option>
              </>)}

              {formData.department === "Marketing" && (<>
                <option value="SEO Specialist">SEO Specialist</option>
                <option value="Content Creator">Content Creator</option>
                <option value="Graphic Designer">Graphic Designer</option>
                <option value="Media Production">Media Production</option>
                <option value="Moderator">Moderator</option>
                <option value="Social Media Specialist">Social Media Specialist</option>
                <option value="Ads Advertising">Ads Advertising</option>
                <option value="Media Buyer">Media Buyer</option>
                <option value="Art Director">Art Director</option>
              </>)}

              {formData.department === "HR" && (
                <option value="HR">HR</option>
              )}

              {/* ✅ أضفنا CEO و COO */}
              {formData.department === "CEO" && (
                <option value="Other">Other</option>
              )}

              {formData.department === "COO" && (
                <option value="Other">Other</option>
              )}

              {formData.department === "Other" && (<>
                <option value="Legal Affairs">Legal Affairs</option>
                <option value="Accountant">Accountant</option>
                <option value="Secretary">Secretary</option>
                <option value="Office Boy">Office Boy</option>
              </>)}
            </select>
          )}

          <select name="role" value={formData.role} onChange={handleChange}>
            <option value={ROLE_VALUES.FOUNDER}>{formatRoleLabel(ROLE_VALUES.FOUNDER)}</option>
            <option value={ROLE_VALUES.MANAGER}>{formatRoleLabel(ROLE_VALUES.MANAGER)}</option>
            <option value={ROLE_VALUES.TEAM_LEADER}>{formatRoleLabel(ROLE_VALUES.TEAM_LEADER)}</option>
            <option value={ROLE_VALUES.EMPLOYEE}>{formatRoleLabel(ROLE_VALUES.EMPLOYEE)}</option>
          </select>

          <button type="submit" disabled={loading}>
            {loading ? "Creating..." : "Create User"}
          </button>

        </form>

        {isPublicSignup && (
          <div className="login-link-text">
            <p>Already have an account?</p>
            <Link to="/login" className="login-page-btn">Go to Login Page</Link>
          </div>
        )}
      </div>

      <FieldToast
        message={fieldToast.message}
        targetRef={fieldToast.targetRef}
        show={fieldToast.show}
      />
    </div>
  );
}
